import {
  EARNED_REFUND_EXCEEDS_ALLOCATION_ERROR,
  EARNED_REFUND_EXCEEDS_BALANCE_ERROR,
  SPLIT_REFUND_EXCEEDS_PAYMENT_ERROR,
  SPLIT_REFUND_REQUIRED_ERROR,
  TRUST_REFUND_EXCEEDS_ALLOCATION_ERROR,
  TRUST_REFUND_EXCEEDS_BALANCE_ERROR,
} from './refundPaymentConstantsV2';

const ALLOCATION_NUMERIC_FIELDS = ['trust_amount', 'earned_amount', 'temporary_funds_amount'];

export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const parseCurrencyInputValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const sanitized = String(value).replace(/[^0-9.-]/g, '');
  if (!sanitized || sanitized === '.' || sanitized === '-') {
    return null;
  }

  return parseAmount(sanitized);
};

/**
 * @param {Object|null|undefined} recordDetails
 * @returns {boolean}
 */
export const isSplitRefundEligible = (recordDetails) => {
  if (!recordDetails) {
    return false;
  }

  const allocation = recordDetails.allocation;
  if (!allocation || typeof allocation !== 'object') {
    return false;
  }

  const trustAmount = parseAmount(allocation.trust_amount) ?? 0;
  const earnedAmount = parseAmount(allocation.earned_amount) ?? 0;

  if (recordDetails.is_split === true) {
    return ALLOCATION_NUMERIC_FIELDS.some((field) => parseAmount(allocation[field]) !== null);
  }

  return trustAmount > 0 || earnedAmount > 0;
};

export const computeTotalRefundAmount = (trustRefundAmount, earnedRefundAmount) => {
  const trust = parseAmount(trustRefundAmount) ?? 0;
  const earned = parseAmount(earnedRefundAmount) ?? 0;
  return trust + earned;
};

export const getTrustAccountIdForRefund = (recordDetails) =>
  recordDetails?.allocation?.trust_account_id || recordDetails?.deposit_account_id || null;

/**
 * @returns {{ isValid: boolean, errors: Object }}
 */
export const validateLegacyRefund = ({ amount, recordDetails, accountBalance }) => {
  const errors = {};
  let isValid = true;
  const paymentRecordAmount = parseAmount(recordDetails?.payment_record_amount) ?? 0;
  const refundAmount = parseAmount(amount);

  if (refundAmount === null || refundAmount < 0) {
    errors.amount = SPLIT_REFUND_REQUIRED_ERROR;
    isValid = false;
  }

  if (refundAmount !== null && refundAmount > paymentRecordAmount) {
    errors.amount = "Refund amount can't be greater than payment record amount";
    isValid = false;
  }

  if (refundAmount !== null && refundAmount > accountBalance) {
    errors.amount = "Refund amount can't be greater than account balance";
    isValid = false;
  }

  return { isValid, errors };
};

/**
 * @returns {{ isValid: boolean, errors: Object }}
 */
export const validateSplitRefund = ({ trustRefundAmount, earnedRefundAmount, recordDetails, trustBalance, operatingBalance }) => {
  const errors = {};
  let isValid = true;
  const trustRefund = parseAmount(trustRefundAmount) ?? 0;
  const earnedRefund = parseAmount(earnedRefundAmount) ?? 0;
  const trustCap = parseAmount(recordDetails?.allocation?.trust_amount) ?? 0;
  const earnedCap = parseAmount(recordDetails?.allocation?.earned_amount) ?? 0;
  const paymentRecordAmount = parseAmount(recordDetails?.payment_record_amount) ?? 0;
  const totalRefund = computeTotalRefundAmount(trustRefundAmount, earnedRefundAmount);

  if (parseAmount(trustRefundAmount) !== null && trustRefund < 0) {
    errors.trust_refund_amount = SPLIT_REFUND_REQUIRED_ERROR;
    isValid = false;
  }

  if (parseAmount(earnedRefundAmount) !== null && earnedRefund < 0) {
    errors.earned_refund_amount = SPLIT_REFUND_REQUIRED_ERROR;
    isValid = false;
  }

  if (trustRefund > trustCap) {
    errors.trust_refund_amount = TRUST_REFUND_EXCEEDS_ALLOCATION_ERROR;
    isValid = false;
  }

  if (earnedRefund > earnedCap) {
    errors.earned_refund_amount = EARNED_REFUND_EXCEEDS_ALLOCATION_ERROR;
    isValid = false;
  }

  if (trustRefund > 0 && trustRefund > trustBalance) {
    errors.trust_refund_amount = TRUST_REFUND_EXCEEDS_BALANCE_ERROR;
    isValid = false;
  }

  if (earnedRefund > 0 && earnedRefund > operatingBalance) {
    errors.earned_refund_amount = EARNED_REFUND_EXCEEDS_BALANCE_ERROR;
    isValid = false;
  }

  if (totalRefund <= 0) {
    errors.total_refund_amount = SPLIT_REFUND_REQUIRED_ERROR;
    isValid = false;
  }

  if (totalRefund > paymentRecordAmount) {
    errors.total_refund_amount = SPLIT_REFUND_EXCEEDS_PAYMENT_ERROR;
    isValid = false;
  }

  return { isValid, errors };
};

export const buildRefundSubmitPayload = ({
  recordDetails,
  notes,
  refundDate,
  selectedPaymentMethod,
  amount,
  trustRefundAmount,
  earnedRefundAmount,
  isSplitRefund,
}) => {
  const basePayload = {
    case_id: recordDetails.case_id,
    case_name: recordDetails.case_name,
    billing_contact_id: recordDetails.billing_contact_id,
    billing_contact_name: recordDetails.billing_contact_name,
    description: notes ? notes : '',
    deposit_into: recordDetails.deposit_into,
    deposit_account_name: recordDetails.deposit_account_name,
    paid_amount: recordDetails.paid_amount,
    due_amount: recordDetails.due_amount,
    amount: recordDetails.amount,
    payment_record_date: refundDate || '',
    payment_method: selectedPaymentMethod,
    payment_ref_id: recordDetails.payment_ref_id,
    payment_ref_number: recordDetails.payment_ref_number,
    payment_record_status: recordDetails.payment_record_status,
    payment_record_type: 'REFUND',
    transaction_details: recordDetails.transaction_details,
    payment_record_ref: recordDetails.payment_record_id,
    payment_for: recordDetails?.payment_for,
  };

  if (isSplitRefund) {
    const totalRefundAmount = computeTotalRefundAmount(trustRefundAmount, earnedRefundAmount);
    return {
      ...basePayload,
      trust_refund_amount: parseAmount(trustRefundAmount) ?? 0,
      earned_refund_amount: parseAmount(earnedRefundAmount) ?? 0,
      total_refund_amount: totalRefundAmount,
      payment_record_amount: totalRefundAmount,
    };
  }

  return {
    ...basePayload,
    payment_record_amount: amount,
  };
};

export const getSplitFullRefundAmounts = (recordDetails) => ({
  trustRefundAmount: parseAmount(recordDetails?.allocation?.trust_amount) ?? 0,
  earnedRefundAmount: parseAmount(recordDetails?.allocation?.earned_amount) ?? 0,
});
